import React from "react";
import {
  Box,
  Container,
  Text,
  Button,
  Grid,
  GridItem,
  VStack,
  HStack,
  Input,
  Icon,


} from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { contactService } from "../firebase/contactService.js";
import { functions } from "../firebase/config.js";
import { httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";

const Hero = () => {
  const MotionBox = motion.div;

  const MiniWizard = () => {
    const navigate = useNavigate();
    const [step, setStep] = React.useState(0);
    const [values, setValues] = React.useState({
      firstName: "",
      phone: "",
      email: "",
      project: "",
    });
    const [isAnimating, setIsAnimating] = React.useState(false);
    const [phoneNumber, setPhoneNumber] = React.useState("");
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [notification, setNotification] = React.useState(null);

    // OTP states
    const [otpCode, setOtpCode] = React.useState("");
    const [otpSent, setOtpSent] = React.useState(false);
    const [otpVerified, setOtpVerified] = React.useState(false);

    // Cloud Functions Twilio
    const sendOTPFn = httpsCallable(functions, "sendOTP");
    const verifyOTPFn = httpsCallable(functions, "verifyOTP");

    const TOTAL_STEPS = 4; // 3 fields + 1 OTP step

    const fields = [
      { key: "firstName", label: "Prenom", placeholder: "Votre prenom" },
      {
        key: "phone",
        label: "Telephone",
        placeholder: "Votre numero de telephone",
      },
      {
        key: "project",
        label: "Projet",
        placeholder: "Votre projet de renovation",
      },
    ];

    const projectOptions = [
      "Panneaux solaires photovoltaiques",
      "Pompe a chaleur",
      "Isolation des combles",
      "Isolation des murs",
      "Chauffe-eau solaire",
      "Changement de fenetres",
      "Ventilation (VMC)",
      "Audit energetique",
      "Renovation globale",
      "Autre projet",
    ];
    const current = step < fields.length ? fields[step] : null;

    const sendOTP = async () => {
      setIsSubmitting(true);
      try {
        const formattedPhone = "+" + phoneNumber;
        await sendOTPFn({ phone: formattedPhone });
        setOtpSent(true);
        setNotification({
          status: "success",
          title: "Code envoye !",
          description: "Un SMS Solarenov avec votre code a ete envoye.",
        });
        setTimeout(() => setNotification(null), 4000);
      } catch (error) {
        console.error("Erreur envoi SMS:", error);
        let message = "Impossible d'envoyer le SMS. Verifiez votre numero.";
        if (error.code === "functions/resource-exhausted") {
          message = "Trop de tentatives. Reessayez dans quelques minutes.";
        } else if (error.code === "functions/invalid-argument") {
          message = "Numero de telephone invalide.";
        }
        setNotification({ status: "error", title: "Erreur", description: message });
        setTimeout(() => setNotification(null), 5000);
      } finally {
        setIsSubmitting(false);
      }
    };

    const verifyOTP = async () => {
      if (otpCode.length < 6) return;
      setIsSubmitting(true);
      try {
        const formattedPhone = "+" + phoneNumber;
        await verifyOTPFn({ phone: formattedPhone, code: otpCode });
        setOtpVerified(true);

        // OTP verifie -> sauvegarder le contact
        const contactData = {
          firstName: values.firstName,
          phone: values.phone,
          email: values.email,
          service: values.project || "Non specifie",
          message: "Projet: " + (values.project || "Non specifie"),
          source: "hero-form",
          phoneVerified: true,
        };

        await contactService.addContact(contactData);
        contactService.sendConfirmationEmail(contactData);
        navigate("/success");
      } catch (error) {
        console.error("Erreur verification OTP:", error);
        let message = "Code incorrect. Veuillez reessayer.";
        if (error.code === "functions/deadline-exceeded") {
          message = "Le code a expire. Renvoyez un nouveau code.";
        } else if (error.code === "functions/resource-exhausted") {
          message = "Trop de tentatives incorrectes. Renvoyez un nouveau code.";
        }
        setNotification({ status: "error", title: "Code invalide", description: message });
        setOtpCode("");
        setTimeout(() => setNotification(null), 5000);
      } finally {
        setIsSubmitting(false);
      }
    };

    const onChange = (e) => {
      const value = e.target.value;
      if (current) {
        setValues({ ...values, [current.key]: value });
      }
    };

    const onPhoneChange = (value, country) => {
      setPhoneNumber(value);
      setValues({ ...values, phone: value });
    };

    const next = () => {
      if (current && current.key === "phone") {
        if (phoneNumber.trim() === "") return;
      } else if (current) {
        if (values[current.key].trim() === "") return;
      }
      setIsAnimating(true);
      setTimeout(() => {
        setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
        setIsAnimating(false);
      }, 300);
    };

    const prev = () => {
      setIsAnimating(true);
      setTimeout(() => {
        setStep((s) => Math.max(s - 1, 0));
        setIsAnimating(false);
      }, 300);
    };

    const onSubmit = async (e) => {
      e.preventDefault();

      // Step OTP: verify code
      if (step === 3) {
        await verifyOTP();
        return;
      }

      // Validation du champ courant
      if (current && current.key === "phone") {
        if (phoneNumber.trim() === "") return;
      } else if (current) {
        if (values[current.key].trim() === "") return;
      }

      // Si derniere etape des champs (project), passer a l'OTP
      if (step === fields.length - 1) {
        setStep(3); // Go to OTP step
        // Envoyer le SMS automatiquement
        setTimeout(() => sendOTP(), 500);
      } else {
        next();
      }
    };

    const isOtpStep = step === 3;

    return (
      <Box as="form" onSubmit={onSubmit} position="relative" overflow="hidden">

        {/* Notifications */}
        {notification && (
          <Box
            bg={
              notification.status === "success"
                ? "rgba(34, 197, 94, 0.95)"
                : "rgba(239, 68, 68, 0.95)"
            }
            color="white"
            borderRadius="2xl"
            p={4}
            mb={6}
            backdropFilter="blur(10px)"
            border="1px solid"
            borderColor="whiteAlpha.300"
          >
            <VStack spacing={2} align="center" w="100%">
              <Text fontSize="2xl">
                {notification.status === "success" ? "\u2705" : "\u274C"}
              </Text>
              <Text fontSize="md" fontWeight={700}>
                {notification.title}
              </Text>
              <Text fontSize="sm">{notification.description}</Text>
            </VStack>
          </Box>
        )}

        {/* Indicateur d'etapes */}
        <HStack mb={6} justify="center" spacing={3} py={2} overflow="visible">
          {Array.from({ length: TOTAL_STEPS }).map((_, index) => (
            <Box
              key={index}
              w="14px"
              h="14px"
              borderRadius="full"
              bg={index <= step ? "#094c26" : "#e5e7eb"}
              transition="all 0.4s ease-in-out"
              boxShadow={index === step ? "0 0 0 3px rgba(9, 76, 38, 0.2)" : "none"}
            />
          ))}
        </HStack>

        {/* Titre simple */}
        <Text
          fontSize="lg"
          color="#1f2937"
          textAlign="center"
          mb={6}
          fontWeight={700}
        >
          {step === 0 && "Comment vous appelez-vous ?"}
          {step === 1 && "Votre numero de telephone ?"}
          {step === 2 && "Quel est votre projet ?"}
          {step === 3 && "\uD83D\uDD12 Verification de votre numero"}
        </Text>

        <AnimatePresence mode="wait">
          <MotionBox
            key={isOtpStep ? "otp" : current ? current.key : "otp"}
            initial={{ opacity: 0, x: isAnimating ? 50 : -50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: isAnimating ? -50 : 50 }}
            transition={{
              duration: 0.3,
              ease: "easeInOut",
            }}
          >
            <VStack spacing={4} align="start">
              {/* OTP Step */}
              {isOtpStep ? (
                <VStack spacing={4} w="full" align="center">
                  <Text fontSize="sm" color="#374151" textAlign="center" fontWeight={500}>
                    Entrez le code a 6 chiffres envoye au{" "}
                    <Text as="span" fontWeight={700}>+{phoneNumber}</Text>
                  </Text>
                  <Input
                    value={otpCode}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
                      setOtpCode(val);
                    }}
                    placeholder="000000"
                    maxLength={6}
                    size="lg"
                    textAlign="center"
                    letterSpacing="0.5em"
                    fontSize="2xl"
                    fontWeight={700}
                    borderRadius="2xl"
                    bg="white"
                    border="2px solid"
                    borderColor="#d1d5db"
                    color="#1f2937"
                    _focus={{
                      borderColor: "#094c26",
                      boxShadow: "0 0 0 3px rgba(9, 76, 38, 0.1)",
                    }}
                    autoFocus
                  />
                  <Button
                    variant="link"
                    color="#094c26"
                    fontSize="sm"
                    fontWeight={600}
                    onClick={sendOTP}
                    isDisabled={isSubmitting}
                  >
                    Renvoyer le code
                  </Button>
                </VStack>
              ) : current && current.key === "phone" ? (
                <Box
                  position="relative"
                  zIndex={10}
                  w="full"
                  sx={{
                    ".react-tel-input": {
                      width: "100% !important",
                      ".form-control": {
                        width: "100% !important",
                        height: "56px !important",
                        borderRadius: "16px !important",
                        border: "2px solid #d1d5db !important",
                        backgroundColor: "white !important",
                        fontSize: "16px !important",
                        fontWeight: "500 !important",
                        color: "#1f2937 !important",
                        paddingLeft: "60px !important",
                        paddingRight: "20px !important",
                        textAlign: "left !important",
                        "&:focus": {
                          borderColor: "#0053b3 !important",
                          boxShadow:
                            "0 0 0 3px rgba(0, 83, 179, 0.1) !important",
                          backgroundColor: "white !important",
                        },
                        "&:hover": {
                          borderColor: "#0053b3 !important",
                          backgroundColor: "white !important",
                        },
                        "&::placeholder": {
                          color: "#6b7280 !important",
                          fontWeight: "400 !important",
                        },
                      },
                      ".flag-dropdown": {
                        border: "2px solid #d1d5db !important",
                        borderRight: "none !important",
                        borderRadius: "16px 0 0 16px !important",
                        backgroundColor: "white !important",
                        "&.open": {
                          borderColor: "#0053b3 !important",
                          backgroundColor: "white !important",
                        },
                        "&:hover": {
                          borderColor: "#0053b3 !important",
                          backgroundColor: "white !important",
                        },
                      },
                      ".selected-flag": {
                        borderRadius: "14px 0 0 14px !important",
                        backgroundColor: "transparent !important",
                        padding: "0 8px !important",
                        "&:hover": {
                          backgroundColor: "transparent !important",
                        },
                        "&:focus": {
                          backgroundColor: "transparent !important",
                        },
                      },
                      ".country-list": {
                        borderRadius: "16px !important",
                        border: "2px solid #e5e7eb !important",
                        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1) !important",
                        maxHeight: "200px !important",
                        overflowY: "auto !important",
                        zIndex: "9999 !important",
                      },
                    },
                  }}
                >
                  <PhoneInput
                    country="fr"
                    value={phoneNumber}
                    onChange={onPhoneChange}
                    placeholder="Votre numero de telephone"
                    enableSearch={true}
                    searchPlaceholder="Rechercher un pays..."
                    autoFocus
                    preferredCountries={[
                      "fr", "us", "de", "gb", "es", "it", "be", "ch", "ca", "ma", "tn", "dz",
                    ]}
                    excludeCountries={[]}
                    containerStyle={{ width: "100%" }}
                    inputStyle={{
                      width: "100%",
                      height: "56px",
                      borderRadius: "16px",
                      border: "2px solid #d1d5db",
                      backgroundColor: "white",
                      fontSize: "16px",
                      fontWeight: "500",
                      color: "#1f2937",
                      paddingLeft: "60px",
                      paddingRight: "20px",
                    }}
                    buttonStyle={{
                      border: "2px solid #d1d5db",
                      borderRight: "none",
                      borderRadius: "16px 0 0 16px",
                      backgroundColor: "white",
                    }}
                    dropdownStyle={{
                      borderRadius: "16px",
                      border: "2px solid #e5e7eb",
                      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                      maxHeight: "200px",
                    }}
                  />
                </Box>
              ) : current && current.key === "project" ? (
                <Box
                  as="select"
                  value={values[current.key]}
                  onChange={onChange}
                  size="lg"
                  borderRadius="2xl"
                  bg="white"
                  border="2px solid"
                  borderColor="#d1d5db"
                  color="#1f2937"
                  fontSize="lg"
                  fontWeight={500}
                  w="100%"
                  h="56px"
                  px={4}
                  _focus={{
                    borderColor: "#094c26",
                    boxShadow: "0 0 0 3px rgba(9, 76, 38, 0.1)",
                    bg: "white",
                    color: "#1f2937",
                    outline: "none",
                  }}
                  _hover={{
                    borderColor: "#094c26",
                    bg: "white",
                  }}
                  autoFocus
                >
                  <option value="">Selectionnez votre projet</option>
                  {projectOptions.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </Box>
              ) : current ? (
                <Input
                  type={current.key === "email" ? "email" : "text"}
                  value={values[current.key]}
                  onChange={onChange}
                  placeholder={current.placeholder}
                  size="lg"
                  borderRadius="2xl"
                  bg="white"
                  border="2px solid"
                  borderColor="#d1d5db"
                  color="#1f2937"
                  fontSize="lg"
                  fontWeight={500}
                  _placeholder={{ color: "#6b7280", fontWeight: 400 }}
                  _focus={{
                    borderColor: "#094c26",
                    boxShadow: "0 0 0 3px rgba(9, 76, 38, 0.1)",
                    bg: "white",
                    color: "#1f2937",
                  }}
                  _hover={{
                    borderColor: "#094c26",
                    bg: "white",
                  }}
                  autoFocus
                />
              ) : null}

              {/* Boutons */}
              <HStack spacing={3} w="full" mt={4}>
                {step > 0 && !isOtpStep && (
                  <Button
                    variant="outline"
                    flex="1"
                    borderColor="#094c26"
                    color="#094c26"
                    _hover={{ bg: "#094c26", color: "white" }}
                    onClick={prev}
                    borderRadius="xl"
                    size="lg"
                    fontWeight={600}
                  >
                    ← Retour
                  </Button>
                )}
                <Button
                  type="submit"
                  flex="1"
                  bg="#094c26"
                  color="white"
                  _hover={{ bg: "#0d6b35" }}
                  fontWeight={700}
                  borderRadius="xl"
                  size="lg"
                  isLoading={isSubmitting}
                  loadingText={isOtpStep ? "Verification..." : "Envoi..."}
                  isDisabled={
                    isOtpStep
                      ? otpCode.length < 6
                      : current && current.key === "phone"
                      ? phoneNumber.trim() === ""
                      : current
                      ? values[current.key].trim() === ""
                      : true
                  }
                  opacity={
                    isOtpStep
                      ? otpCode.length < 6 ? 0.5 : 1
                      : current && current.key === "phone"
                      ? phoneNumber.trim() === "" ? 0.5 : 1
                      : current && values[current.key].trim() === ""
                      ? 0.5
                      : 1
                  }
                  _disabled={{ bg: "#9ca3af", color: "white" }}
                >
                  {isOtpStep
                    ? "Valider le code"
                    : step < fields.length - 1
                    ? "Continuer →"
                    : "Envoyer le code SMS →"}
                </Button>
              </HStack>
            </VStack>
          </MotionBox>
        </AnimatePresence>

        {/* Progression */}
        <Text
          fontSize="sm"
          color="#374151"
          textAlign="center"
          mt={6}
          fontWeight={500}
        >
          {step + 1} sur {TOTAL_STEPS}
        </Text>
      </Box>
    );
  };

  const FormCard = () => (
    <Box
      borderRadius="3xl"
      overflow="hidden"
      boxShadow="0 25px 80px rgba(9, 76, 38, 0.15)"
      bg="rgba(255, 255, 255, 0.82)"
      border="1px solid"
      borderColor="whiteAlpha.500"
      backdropFilter="blur(8px)"
      p={{ base: 6, lg: 8 }}
      position="relative"
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="linear-gradient(135deg, rgba(9, 76, 38, 0.02) 0%, rgba(5, 150, 105, 0.02) 100%)"
        pointerEvents="none"
      />
      <VStack align="stretch" spacing={6} position="relative" zIndex={1}>
        <VStack spacing={2}>
          <Text fontWeight={800} fontSize="xl" color="#094c26">
            Devis gratuit et personnalise
          </Text>
          <Text
            fontSize="sm"
            color="#374151"
            textAlign="center"
            fontWeight={500}
          >
            Un expert Groupe Solarenov vous rappelle sous 24h
          </Text>
        </VStack>
        <MiniWizard />
      </VStack>
    </Box>
  );

  return (
    <Box as="section" bg="transparent" mt={10} mb={{ base: 0, lg: 10 }}>
      <Container maxW="1200px" px={5} py={{ base: 12, lg: 20 }}>
        <Grid
          templateColumns={{ base: "1fr", lg: "1.1fr 0.9fr" }}
          gap={{ base: 10, lg: 16 }}
          alignItems="center"
        >
          <GridItem>
            <VStack align="start" spacing={6}>

              <Text
                as="h1"
                fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                fontWeight={800}
                lineHeight="1.15"
                color="white"
                mb={{ base: 3, lg: 4 }}
              >
                L'energie solaire francaise à votre service
              </Text>
              <Text
                fontSize={{ base: "lg", md: "xl" }}
                color="white"
                fontWeight={500}
                mb={{ base: 4, lg: 6 }}
                opacity={0.95}
              >
                Pour un avenir plus vert, durable et moins couteux. Groupe Solarenov vous accompagne sur l'ensemble du territoire.
              </Text>
              {/* Liste des avantages */}
              <VStack
                align="start"
                spacing={3}
                color="white"
                fontWeight="bold"
                mt={{ base: 4, lg: 6 }}
                textAlign="left"
              >
                <Box pl={3} borderLeft="4px solid" borderColor="#094c26">
                  <VStack align="start" spacing={2}>
                    <HStack spacing={2}>
                      <Text fontSize={{ base: 'xl', lg: '2xl' }}>{"\u2713"}</Text>
                      <Text fontSize={{ base: 'md', lg: 'lg' }} fontWeight={600}>Jusqu'a 90% de subventions</Text>
                    </HStack>
                    <HStack spacing={2}>
                      <Text fontSize={{ base: 'xl', lg: '2xl' }}>{"\u2713"}</Text>
                      <Text fontSize={{ base: 'md', lg: 'lg' }} fontWeight={600}>Materiel haut de gamme exclusif</Text>
                    </HStack>
                    <HStack spacing={2}>
                      <Text fontSize={{ base: 'xl', lg: '2xl' }}>{"\u2713"}</Text>
                      <Text fontSize={{ base: 'md', lg: 'lg' }} fontWeight={600}>Techniciens certifies RGE QUALI PV</Text>
                    </HStack>
                    <HStack spacing={2}>
                      <Text fontSize={{ base: 'xl', lg: '2xl' }}>{"\u2713"}</Text>
                      <Text fontSize={{ base: 'md', lg: 'lg' }} fontWeight={600}>Reduction de votre facture d'electricite</Text>
                    </HStack>
                  </VStack>
                </Box>
              </VStack>
            </VStack>
          </GridItem>
          <GridItem display={{ base: "none", lg: "block" }}>
            <FormCard />
          </GridItem>
        </Grid>
      </Container>

      {/* Banderolle des avantages */}
      <Box px={5} mt={{ base: 6, lg: 10 }} display={{ base: 'none', md: 'block' }}>
        <Box
          maxW="1200px"
          mx="auto"
          bg="rgba(255, 255, 255, 0.85)"
          border="1px solid"
          borderColor="whiteAlpha.600"
          boxShadow="0 10px 30px rgba(0,0,0,0.08)"
          backdropFilter="blur(6px)"
          borderRadius="2xl"
          px={{ base: 4, md: 8 }}
          py={{ base: 3, md: 4 }}
        >
          <HStack
            spacing={{ base: 6, md: 12, lg: 16 }}
            justify={{ base: "center", md: "space-between" }}
            align="center"
            flexWrap={{ base: "wrap", md: "nowrap" }}
            w="100%"
          >
            <Text fontWeight={800} color="#094c26" fontSize={{ base: "sm", md: "md" }}>{"\u2713"} Devis sous 24h</Text>
            <Text display={{ base: "none", md: "block" }} color="black" fontSize="2xl">{"\u2022"}</Text>
            <Text fontWeight={800} color="#094c26" fontSize={{ base: "sm", md: "md" }}>{"\u2713"} Aides jusqu'a 90%</Text>
            <Text display={{ base: "none", md: "block" }} color="black" fontSize="2xl">{"\u2022"}</Text>
            <Text fontWeight={800} color="#094c26" fontSize={{ base: "sm", md: "md" }}>{"\u2713"} Techniciens RGE QUALI PV</Text>
            <Text display={{ base: "none", md: "block" }} color="black" fontSize="2xl">{"\u2022"}</Text>
            <Text fontWeight={800} color="#094c26" fontSize={{ base: "sm", md: "md" }}>{"\u2713"} Garantie decennale</Text>
          </HStack>
        </Box>
      </Box>
      {/* Formulaire mobile */}
      <Box
        id="mobile-form"
        display={{ base: "block", lg: "none" }}
        px={0}
        mt={4}
      >
        <Box
          position="relative"
          left="50%"
          ml="-50vw"
          w="100vw"
          bg="linear-gradient(135deg, #094c26, #0d6b35, #063a1d)"
          py={8}
          mb={{ base: -1, lg: 0 }}
        >
          <Container maxW="1200px" px={5}>
            <FormCard />
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default Hero;
